import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, orderBy, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { db } from '../config';
import type { PostComment, CommentVisibility, CommentAuthorRole } from './types';

export interface CreateCommentInput {
    postId: string;
    creatorId: string;
    authorId: string;
    authorDisplayName?: string;
    authorRole: CommentAuthorRole;
    text: string;
    visibility: CommentVisibility;
    threadId?: string; // omitted for root comments
    parentId?: string | null; // null or omitted for root
    participantIds?: string[]; // optional override
}

export async function addComment(input: CreateCommentInput): Promise<string> {
    const {
        postId,
        creatorId,
        authorId,
        authorDisplayName,
        authorRole,
        text,
        visibility
    } = input;

    const commentsRef = collection(db, 'posts', postId, 'comments');

    // Create a temporary doc to get an id if threadId not provided
    if (!input.threadId) {
        const rootRef = doc(commentsRef);
        const newId = rootRef.id;
        const data: Omit<PostComment, 'id'> = {
            postId,
            creatorId,
            authorId,
            authorDisplayName,
            authorRole,
            text,
            visibility,
            threadId: newId,
            parentId: null,
            participantIds: visibility === 'private' ? [creatorId, authorId] : [creatorId],
            createdAt: serverTimestamp(),
        };
        await setDoc(rootRef, data as any);
        return newId;
    } else {
        // Reply in an existing thread
        const data: Omit<PostComment, 'id'> = {
            postId,
            creatorId,
            authorId,
            authorDisplayName,
            authorRole,
            text,
            visibility,
            threadId: input.threadId,
            parentId: input.parentId ?? input.threadId,
            participantIds: visibility === 'private' ? [creatorId, authorId] : [creatorId],
            createdAt: serverTimestamp(),
        };
        const docRef = await addDoc(commentsRef, data as any);
        return docRef.id;
    }
}

export async function updateComment(postId: string, commentId: string, updates: Partial<Pick<PostComment, 'text' | 'visibility'>>): Promise<void> {
    const ref = doc(db, 'posts', postId, 'comments', commentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Comment not found');
    const data = snap.data() as any;
    const payload: Record<string, any> = {};
    if (typeof updates.text === 'string') payload.text = updates.text;
    if (updates.visibility) {
        payload.visibility = updates.visibility;
        payload.participantIds = updates.visibility === 'private'
            ? Array.from(new Set([data.creatorId, data.authorId]))
            : [data.creatorId];
    }
    payload.updatedAt = serverTimestamp();
    await updateDoc(ref, payload);
}

export async function deleteComment(postId: string, commentId: string): Promise<void> {
    try {
        const ref = doc(db, 'posts', postId, 'comments', commentId);
        await deleteDoc(ref);
    } catch (e: any) {
        console.error('deleteComment error', e);
        throw new Error(e?.message || 'Failed to delete comment');
    }
}

export interface ListCommentsOptions {
    includePrivateForUserId?: string; // when present, include user's private threads
}

export async function listComments(postId: string, opts: ListCommentsOptions = {}): Promise<PostComment[]> {
    const commentsRef = collection(db, 'posts', postId, 'comments');
    const queries = [] as any[];
    // Public comments
    queries.push(getDocs(query(commentsRef, where('visibility', '==', 'public'), orderBy('createdAt', 'asc'))));
    // Private comments for viewer
    if (opts.includePrivateForUserId) {
        queries.push(
            getDocs(
                query(
                    commentsRef,
                    where('visibility', '==', 'private'),
                    where('participantIds', 'array-contains', opts.includePrivateForUserId),
                    orderBy('createdAt', 'asc')
                )
            )
        );
    }
    const snapshots = await Promise.all(queries);
    const results: PostComment[] = [];
    snapshots.forEach((snap) => {
        snap.forEach((d: any) => results.push({ id: d.id, ...(d.data() as any) }));
    });
    // Sort by createdAt in case of mixed results
    results.sort((a, b) => (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0));
    return results;
}

export function subscribeToComments(
    postId: string,
    opts: ListCommentsOptions,
    callback: (comments: PostComment[]) => void,
    onError?: (error: any) => void
) {
    const commentsRef = collection(db, 'posts', postId, 'comments');
    const publicQuery = query(commentsRef, where('visibility', '==', 'public'));

    const unsubscribers: Array<() => void> = [];
    const aggregated: Record<string, PostComment> = {};

    const flush = () => {
        const arr = Object.values(aggregated).sort((a, b) => (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0));
        callback(arr);
    };

    unsubscribers.push(
        onSnapshot(
            publicQuery,
            (snap) => {
                snap.docChanges().forEach((change) => {
                    aggregated[change.doc.id] = { id: change.doc.id, ...(change.doc.data() as any) };
                });
                flush();
            },
            (err) => {
                if (onError) onError(err);
            }
        )
    );

    if (opts.includePrivateForUserId) {
        const privateQuery = query(
            commentsRef,
            where('visibility', '==', 'private'),
            where('participantIds', 'array-contains', opts.includePrivateForUserId)
        );
        unsubscribers.push(
            onSnapshot(
                privateQuery,
                (snap) => {
                    snap.docChanges().forEach((change) => {
                        aggregated[change.doc.id] = { id: change.doc.id, ...(change.doc.data() as any) };
                    });
                    flush();
                },
                (err) => {
                    if (onError) onError(err);
                }
            )
        );
    }

    return () => unsubscribers.forEach((u) => u());
}


